
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, Clock, Play, CheckCircle2, XCircle, SkipForward, RotateCcw, ListChecks, Trophy, Layers, Replace, Settings, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { User, TournamentRoundType, TournamentTrick, TrickStatus, TournamentResult, RoundConfig } from '../types';
import confetti from 'canvas-confetti';
import { storage } from '../storage';
import threeMinuteCountdownAudio from '../voice/3fenzhong2.MP3';

interface TournamentGameProps {
  user: User;
  onUpdateUser: (updatedUser: User) => void;
  onBack: () => void;
  volume: number;
}

const COUNTDOWN_AUDIO_BOOST = 1.5;
const ROUND_DURATION_MS = 180000;

// 结算按钮填充边缘发散的粗粒子（top：纵向位置百分比；size：粒子边长；dx/dy：发散位移；delay/duration：动画时序；color：粒子颜色；dark：黑色粒子标记）
const FILL_EDGE_PARTICLES: { top: number; size: number; dx: number; dy: number; delay: number; duration: number; color?: string; dark?: boolean }[] = [
    { top: 1, size: 6, dx: 10, dy: -15, delay: 180, duration: 920 },
    { top: 4, size: 5, dx: 12, dy: -16, delay: 0, duration: 950 },
    { top: 8, size: 8, dx: -13, dy: -8, delay: 340, duration: 1180, color: '#0a0a0a', dark: true },
    { top: 12, size: 8, dx: 16, dy: -6, delay: 380, duration: 1150 },
    { top: 15, size: 7, dx: -11, dy: -10, delay: 240, duration: 1060, color: '#fde047' },
    { top: 17, size: 6, dx: 4, dy: 13, delay: 660, duration: 900, color: '#0a0a0a', dark: true },
    { top: 19, size: 4, dx: -9, dy: -12, delay: 720, duration: 800, color: '#fde047' },
    { top: 22, size: 5, dx: 17, dy: 7, delay: 60, duration: 1010 },
    { top: 26, size: 9, dx: 20, dy: 4, delay: 140, duration: 1250 },
    { top: 28, size: 8, dx: -19, dy: -5, delay: 760, duration: 1190 },
    { top: 30, size: 7, dx: -18, dy: 5, delay: 480, duration: 1050, color: '#0a0a0a', dark: true },
    { top: 33, size: 5, dx: -12, dy: 8, delay: 560, duration: 900, color: '#f59e0b' },
    { top: 36, size: 6, dx: 13, dy: 12, delay: 420, duration: 980, color: '#f59e0b' },
    { top: 40, size: 7, dx: 14, dy: -14, delay: 880, duration: 1050 },
    { top: 44, size: 9, dx: -15, dy: 10, delay: 100, duration: 1220, color: '#0a0a0a', dark: true },
    { top: 47, size: 4, dx: 9, dy: 16, delay: 260, duration: 850, color: '#fffbeb' },
    { top: 50, size: 9, dx: -17, dy: 3, delay: 940, duration: 1240 },
    { top: 53, size: 8, dx: -16, dy: -4, delay: 640, duration: 1200 },
    { top: 57, size: 6, dx: -7, dy: -15, delay: 820, duration: 960, color: '#0a0a0a', dark: true },
    { top: 60, size: 6, dx: 18, dy: 10, delay: 60, duration: 1000, color: '#fde047' },
    { top: 63, size: 5, dx: 16, dy: -12, delay: 300, duration: 870, color: '#fde047' },
    { top: 67, size: 4, dx: -8, dy: 14, delay: 460, duration: 780 },
    { top: 70, size: 8, dx: 6, dy: -19, delay: 700, duration: 1130, color: '#0a0a0a', dark: true },
    { top: 73, size: 9, dx: 15, dy: -8, delay: 800, duration: 1300, color: '#f59e0b' },
    { top: 76, size: 8, dx: -13, dy: 15, delay: 20, duration: 1210 },
    { top: 80, size: 5, dx: -14, dy: -18, delay: 200, duration: 880 },
    { top: 84, size: 7, dx: -11, dy: 9, delay: 40, duration: 1020, color: '#0a0a0a', dark: true },
    { top: 86, size: 7, dx: 11, dy: 6, delay: 520, duration: 1120, color: '#fde047' },
    { top: 89, size: 6, dx: 12, dy: -17, delay: 860, duration: 940 },
    { top: 91, size: 5, dx: -6, dy: 17, delay: 600, duration: 890, color: '#0a0a0a', dark: true },
    { top: 92, size: 4, dx: -10, dy: 12, delay: 900, duration: 820 },
    { top: 95, size: 5, dx: -9, dy: 11, delay: 400, duration: 900, color: '#fffbeb' },
    { top: 97, size: 6, dx: 13, dy: -6, delay: 320, duration: 1080 },
    { top: 99, size: 7, dx: 15, dy: 8, delay: 540, duration: 1160 },
];
const getFinalBonusForScore = (score: number) => {
  if (score === 13) return 30;
  if (score === 14) return 100;
  if (score === 15) return 225;
  return 0;
};

// 简单的音频合成器
const playBeep = (freq: number, duration: number, volume: number) => {
  if (volume <= 0) return;
  try {
    const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
    const audioCtx = new AudioContext();
    const oscillator = audioCtx.createOscillator();
    const gainNode = audioCtx.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioCtx.destination);

    const now = audioCtx.currentTime;
    gainNode.gain.setValueAtTime(volume, now);
    gainNode.gain.exponentialRampToValueAtTime(0.01, now + duration);

    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(freq, now);

    oscillator.start();
    oscillator.stop(now + duration);
  } catch (e) {
    console.warn("Audio play failed:", e);
  }
};

const TournamentGame: React.FC<TournamentGameProps> = ({ user, onUpdateUser, onBack, volume }) => {
  const TOURNAMENT_DEBUG_FLAG = 'kendama-debug-tournament-start';
  const [phase, setPhase] = useState<'SELECT_CATEGORY' | 'SELECT_PROFILE' | 'COUNTDOWN' | 'PLAYING' | 'FINISHED'>('SELECT_CATEGORY');
  const [countdown, setCountdown] = useState(3);
  const [selectedCategory, setSelectedCategory] = useState<TournamentRoundType>('PRELIM1');
  const [selectedProfile, setSelectedProfile] = useState<RoundConfig | null>(null);
  const [initialScore, setInitialScore] = useState<string>('0');
  const [timeLeft, setTimeLeft] = useState(ROUND_DURATION_MS); 
  const [trickStatuses, setTrickStatuses] = useState<Record<string, TrickStatus>>({});
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const navigate = useNavigate();
  
  const config = user.tournamentData!;
  const savedRef = useRef(false);
  const countdownAudioRef = useRef<HTMLAudioElement | null>(null);
  const reportDebug = (hypothesisId: 'A' | 'B' | 'C' | 'D', location: string, msg: string, data: Record<string, unknown>) => {
    // #region debug-point report
    try {
      if (typeof window === 'undefined' || window.localStorage.getItem(TOURNAMENT_DEBUG_FLAG) !== '1') {
        return;
      }
    } catch {
      return;
    }

    const payload = JSON.stringify({
      sessionId: 'tournament-start-crash',
      runId: 'pre-fix',
      hypothesisId,
      location,
      msg: `[DEBUG] ${msg}`,
      data,
      ts: Date.now()
    });

    try {
      if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon('http://127.0.0.1:7778/event', blob);
        return;
      }

      void fetch('http://127.0.0.1:7778/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      }).catch(() => {});
    } catch {
      // 调试上报失败不应影响比赛流程
    }
    // #endregion
  };

  const getCategoryDisplayName = (cat: TournamentRoundType) => {
      if (cat === 'PRELIM1') return 'PRELIM 1 / 初赛 1';
      if (cat === 'PRELIM2') return 'PRELIM 2 / 初赛 2';
      return 'FINALS / 决赛';
  };

  const profiles = useMemo(() => {
    if (selectedCategory === 'PRELIM1') return config.prelim1Configs;
    if (selectedCategory === 'PRELIM2') return config.prelim2Configs;
    return config.finalConfigs;
  }, [selectedCategory, config]);

  const alternativeTrickObj = useMemo(() => {
    if (!selectedProfile?.alternativeTrick) return null;
    const sel = selectedProfile.alternativeTrick;
    const ml = config.masterLists.find(l => l.id === sel.masterListId);
    return ml?.tricks.find(t => t.id === sel.trickId) || null;
  }, [selectedProfile, config.masterLists]);

  const currentTrickList = useMemo(() => {
      if (!selectedProfile) return [];
      
      const baseTricks = selectedProfile.selectedTricks.map(sel => {
          const ml = config.masterLists.find(l => l.id === sel.masterListId);
          return ml?.tricks.find(t => t.id === sel.trickId);
      }).filter(Boolean) as TournamentTrick[];

      if (alternativeTrickObj) {
          return [...baseTricks, alternativeTrickObj];
      }

      return baseTricks;
  }, [selectedProfile, config.masterLists, alternativeTrickObj]);

  const regularTricks = useMemo(() => {
      if (!alternativeTrickObj) return currentTrickList;
      return currentTrickList.filter(t => t.id !== alternativeTrickObj.id);
  }, [currentTrickList, alternativeTrickObj]);

  const shouldIgnoreAlternativeInPrelimScore = useMemo(() => {
      if (selectedCategory === 'FINAL' || !alternativeTrickObj) return false;
      return regularTricks.length > 0 && regularTricks.every(t => trickStatuses[t.id] === 'success');
  }, [selectedCategory, alternativeTrickObj, regularTricks, trickStatuses]);

  const calculateTotalScore = () => {
      let total = 0;
      if (selectedCategory === 'FINAL') {
          let baseSum = 0;
          let bonus = 0;
          let allTricksRawSum = 0;
          currentTrickList.forEach(trick => {
              allTricksRawSum += trick.score;
              if (trickStatuses[trick.id] === 'success') {
                  baseSum += Math.pow(trick.score, 2);
                  bonus += getFinalBonusForScore(trick.score);
              }
          });
          const allSuccess = currentTrickList.length > 0 && currentTrickList.every(t => trickStatuses[t.id] === 'success');
          total = baseSum + parseInt(initialScore || '0') + bonus;
          if (allSuccess) total += allTricksRawSum;
      } else {
          const scoringTricks = shouldIgnoreAlternativeInPrelimScore ? regularTricks : currentTrickList;
          scoringTricks.forEach(trick => {
              if (trickStatuses[trick.id] === 'success') total += trick.score;
          });
      }
      return total;
  };

  const currentScore = calculateTotalScore();

  const completedTrickCount = useMemo(
      () => currentTrickList.filter(t => trickStatuses[t.id] === 'success').length,
      [currentTrickList, trickStatuses]
  );

  // 结算按钮的填充进度：随时间消耗从 0% 增长到 100%（时间到即填满）
  const roundFillPercent = Math.min(100, Math.max(0, ((ROUND_DURATION_MS - timeLeft) / ROUND_DURATION_MS) * 100));

  // 倒计时音效监听
  useEffect(() => {
    if (phase === 'COUNTDOWN') {
      // #region debug-point A:countdown-effect
      reportDebug('A', 'TournamentGame.tsx:countdown-beep-effect', 'countdown effect triggered', {
        phase,
        countdown,
        volume
      });
      // #endregion
      if (countdown > 0) {
        playBeep(880, 0.1, volume); // 中频嘀
      } else if (countdown === 0) {
        playBeep(1320, 0.3, volume); // 高频嘀
      }
    }
  }, [countdown, phase, volume]);

  useEffect(() => {
      if (phase === 'COUNTDOWN') {
          // #region debug-point C:countdown-transition
          reportDebug('C', 'TournamentGame.tsx:countdown-transition-effect', 'countdown transition effect entered', {
              phase,
              countdown
          });
          // #endregion
          savedRef.current = false;
          if (countdown > 0) {
              const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
              return () => clearTimeout(timer);
          } else {
              if (typeof window !== 'undefined' && volume > 0) {
                  if (!countdownAudioRef.current) {
                      countdownAudioRef.current = new Audio(threeMinuteCountdownAudio);
                      countdownAudioRef.current.preload = 'auto';
                  }
                  countdownAudioRef.current.volume = Math.max(0, Math.min(1, volume * COUNTDOWN_AUDIO_BOOST));
                  countdownAudioRef.current.currentTime = 0;
                  countdownAudioRef.current.play().catch((error) => {
                      reportDebug('B', 'TournamentGame.tsx:countdown-transition-effect', 'early countdown audio playback failed', {
                          volume,
                          error: error instanceof Error ? error.message : String(error)
                      });
                  });
              }

              const startPlayingTimer = setTimeout(() => {
                  setPhase('PLAYING');
                  setTimeLeft(ROUND_DURATION_MS);
              }, 500);

              return () => clearTimeout(startPlayingTimer);
          }
      }
  }, [phase, countdown, volume]);

  useEffect(() => {
      let interval: any;
      if (phase === 'PLAYING') {
          const startTime = Date.now();
          interval = setInterval(() => {
              const elapsed = Date.now() - startTime;
              const remaining = Math.max(0, ROUND_DURATION_MS - elapsed);
              setTimeLeft(remaining);
              if (remaining === 0) {
                  clearInterval(interval);
              }
          }, 33);
      }
      return () => clearInterval(interval);
  }, [phase]);

  useEffect(() => {
      if (typeof window === 'undefined') return;

      const stopCountdownAudio = () => {
          if (!countdownAudioRef.current) return;
          countdownAudioRef.current.pause();
          countdownAudioRef.current.currentTime = 0;
      };

      if (phase !== 'PLAYING' || volume <= 0) {
          // #region debug-point B:audio-skip
          reportDebug('B', 'TournamentGame.tsx:playing-audio-effect', 'countdown audio skipped or stopped', {
              phase,
              volume,
              hasAudioRef: !!countdownAudioRef.current
          });
          // #endregion
          stopCountdownAudio();
          return;
      }

      if (!countdownAudioRef.current) {
          // #region debug-point B:audio-create
          reportDebug('B', 'TournamentGame.tsx:playing-audio-effect', 'creating countdown audio instance', {
              phase,
              volume,
              src: threeMinuteCountdownAudio
          });
          // #endregion
          countdownAudioRef.current = new Audio(threeMinuteCountdownAudio);
          countdownAudioRef.current.preload = 'auto';
      }

      countdownAudioRef.current.volume = Math.max(0, Math.min(1, volume * COUNTDOWN_AUDIO_BOOST));
      // #region debug-point B:audio-play
      reportDebug('B', 'TournamentGame.tsx:playing-audio-effect', 'attempting countdown audio playback', {
          phase,
          volume,
          currentTime: countdownAudioRef.current.currentTime,
          readyState: countdownAudioRef.current.readyState
      });
      // #endregion
      if (countdownAudioRef.current.paused) {
          countdownAudioRef.current.currentTime = 0;
          countdownAudioRef.current.play().catch((error) => {
              // #region debug-point B:audio-play-failed
              reportDebug('B', 'TournamentGame.tsx:playing-audio-effect', 'countdown audio playback failed', {
                  phase,
                  volume,
                  error: error instanceof Error ? error.message : String(error)
              });
              // #endregion
              console.warn('Countdown music play failed:', error);
          });
      }

      return () => {
          stopCountdownAudio();
      };
  }, [phase, volume]);

  useEffect(() => {
      if (phase === 'PLAYING' && currentTrickList.length > 0) {
          const allRegularSuccess = regularTricks.length > 0 && regularTricks.every(t => trickStatuses[t.id] === 'success');

          // 只有正常招全部成功才自动进入结算。
          // 时间走完后不自动结算，保持压哨状态，等待用户手动点击"结束并结算分数"。
          if (allRegularSuccess) {
              setPhase('FINISHED');
              if (currentScore > 0) {
                  confetti({ 
                      particleCount: 150, 
                      spread: 70, 
                      origin: { y: 0.6 }, 
                      colors: ['#fbbf24', '#ffffff', '#a855f7'] 
                  });
              }
          }
      }
  }, [trickStatuses, currentTrickList, phase, currentScore, regularTricks, timeLeft]);



  useEffect(() => {
      if (phase === 'FINISHED' && !savedRef.current && selectedProfile) {
          savedRef.current = true;
          const result: TournamentResult = {
              id: Date.now().toString(),
              timestamp: Date.now(),
              roundCategory: selectedCategory,
              roundName: selectedProfile.name,
              totalScore: currentScore,
              initialScore: selectedCategory === 'FINAL' ? parseInt(initialScore || '0') : undefined,
              tricks: currentTrickList.map(t => ({
                  trickId: t.id,
                  trickName: t.name,
                  baseScore: t.score,
                  status: trickStatuses[t.id] || 'pending'
              }))
          };
          const newHistory = [result, ...(user.tournamentHistory || [])];
          const updatedUser = { ...user, tournamentHistory: newHistory };
          onUpdateUser(updatedUser);
          storage.save(updatedUser);
      }
  }, [phase, currentScore, currentTrickList, initialScore, selectedCategory, selectedProfile, trickStatuses, user, onUpdateUser]);

  const handleStart = () => {
      // #region debug-point A:handle-start
      reportDebug('A', 'TournamentGame.tsx:handleStart', 'handleStart invoked', {
          hasSelectedProfile: !!selectedProfile,
          currentTrickListLength: currentTrickList.length,
          phase,
          volume
      });
      // #endregion
      if (!selectedProfile || currentTrickList.length === 0) {
          alert("This configuration has no tricks!");
          return;
      }
      setTrickStatuses({});
      setCountdown(3);
      setPhase('COUNTDOWN');
      // #region debug-point A:handle-start-after
      reportDebug('A', 'TournamentGame.tsx:handleStart', 'handleStart state updates queued', {
          nextPhase: 'COUNTDOWN',
          nextCountdown: 3
      });
      // #endregion
  };

  const handleManualFinish = () => {
      if (countdownAudioRef.current) {
          countdownAudioRef.current.pause();
          countdownAudioRef.current.currentTime = 0;
      }
      setPhase('FINISHED');
      if (currentScore > 0) {
          confetti({ 
              particleCount: 150, 
              spread: 70, 
              origin: { y: 0.6 }, 
              colors: ['#fbbf24', '#ffffff', '#a855f7'] 
          });
      }
  };

  const handleUpdateStatus = (trickId: string, status: TrickStatus) => {
      setTrickStatuses(prev => ({...prev, [trickId]: status}));
  };

  const formatTime = (ms: number) => {
      const seconds = Math.floor(ms / 1000);
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      const cs = Math.floor((ms % 1000) / 10);
      return `${m}:${s.toString().padStart(2, '0')}.${cs.toString().padStart(2, '0')}`;
  };

  // 比赛进行中（倒计时/正式比赛）点击返回时先弹窗确认，避免误触退出丢失进度
  const handleBackRequest = () => {
      if (phase === 'COUNTDOWN' || phase === 'PLAYING') {
          setShowExitConfirm(true);
          return;
      }
      onBack();
  };

  const exitConfirmModal = showExitConfirm ? (
      <div className="absolute inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-md animate-in fade-in duration-200 p-4">
          <div className="w-full max-w-sm bg-zinc-900 border-2 border-yellow-500/70 rounded-2xl shadow-[0_0_40px_rgba(234,179,8,0.2)] p-6 flex flex-col items-center text-center">
              <AlertTriangle className="text-yellow-500 mb-3" size={36} />
              <h3 className="text-xl font-orbitron font-bold text-white uppercase tracking-widest">Leave Match?</h3>
              <p className="text-zinc-400 font-rajdhani mt-2 mb-6">比赛正在进行，确定要返回吗？当前进度将不会保存。</p>
              <div className="flex w-full gap-3">
                  <button onClick={() => setShowExitConfirm(false)} className="flex-1 py-3 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-orbitron text-sm uppercase tracking-wider transition-colors">取消 / Cancel</button>
                  <button onClick={onBack} className="flex-1 py-3 rounded-xl bg-red-600 hover:bg-red-500 text-white font-orbitron text-sm uppercase tracking-wider transition-colors">确定 / Confirm</button>
              </div>
          </div>
      </div>
  ) : null;

  if (phase === 'SELECT_CATEGORY') {
      return (
          <div className="w-full max-w-md mx-auto p-4 flex flex-col items-center justify-center h-full gap-4 relative pt-10">
              <button onClick={onBack} className="absolute top-4 left-4 text-zinc-500 hover:text-white transition-colors z-10"><ArrowLeft size={24} /></button>
              
              <h1 className="text-3xl font-orbitron font-bold text-yellow-500 uppercase tracking-widest text-center neon-glow-blue mb-4">TOURNAMENT ROUND</h1>
              <div className="flex flex-col gap-3 w-full">
                  {(['PRELIM1', 'PRELIM2', 'FINAL'] as TournamentRoundType[]).map(cat => (
                      <button
                          key={cat}
                          onClick={() => { setSelectedCategory(cat); setPhase('SELECT_PROFILE'); }}
                          className="group relative p-6 rounded-2xl bg-zinc-900 border-2 border-zinc-800 hover:border-yellow-500 transition-all flex items-center gap-4 overflow-hidden"
                      >
                          <div className="absolute inset-0 bg-yellow-500/0 group-hover:bg-yellow-500/5 transition-colors"></div>
                          <div className={`p-4 rounded-xl ${cat === 'FINAL' ? 'bg-yellow-900/30 text-yellow-500' : 'bg-zinc-800 text-zinc-400'}`}>
                             {cat === 'FINAL' ? <Trophy size={24} /> : <Layers size={24} />}
                          </div>
                          <div className="flex flex-col items-start">
                             <span className="text-xl font-orbitron font-bold text-white tracking-widest text-left">
                                {cat === 'PRELIM1' ? 'PRELIM 1 / 初赛 1' : cat === 'PRELIM2' ? 'PRELIM 2 / 初赛 2' : 'FINALS / 决赛'}
                             </span>
                             <span className="text-[10px] text-zinc-500 font-rajdhani uppercase tracking-wider text-left mt-1">
                                {cat === 'FINAL' ? '30 Trick Max | Squared Score / 30招 | 乘算分' : '10 Trick Max | Flat Score / 10招 | 基础分'}
                             </span>
                          </div>
                      </button>
                  ))}
              </div>
          </div>
      );
  }

  if (phase === 'SELECT_PROFILE') {
      return (
          <div className="w-full max-w-2xl mx-auto p-4 flex flex-col h-full overflow-hidden relative">
              <div className="shrink-0 flex justify-between items-center mb-6">
                <button onClick={() => setPhase('SELECT_CATEGORY')} className="flex items-center gap-2 text-zinc-500 hover:text-white font-orbitron text-xs uppercase tracking-widest"><ArrowLeft size={16} /> Back to Category</button>
              </div>
              
              <h2 className="shrink-0 text-2xl font-orbitron font-bold text-white mb-2 uppercase tracking-widest text-center">{getCategoryDisplayName(selectedCategory)}</h2>
              <p className="shrink-0 text-center text-zinc-500 font-rajdhani mb-8">Select a configuration profile to begin / 选择比赛方案</p>

              <div className="flex-1 flex flex-col space-y-3 pr-2 min-h-0">
                  {profiles.map(profile => {
                      const isSelected = selectedProfile?.id === profile.id;
                      
                      let maxScore = 0;
                      let squaredScore = 0;
                      let bonus = 0;
                      
                      const allTricks = profile.selectedTricks.map(sel => {
                          const ml = config.masterLists.find(l => l.id === sel.masterListId);
                          return ml?.tricks.find(t => t.id === sel.trickId);
                      }).filter(Boolean) as TournamentTrick[];
                      
                      allTricks.forEach(trick => {
                          maxScore += trick.score;
                          squaredScore += Math.pow(trick.score, 2);
                          bonus += getFinalBonusForScore(trick.score);
                      });

                      const displayScore = selectedCategory === 'FINAL' ? (squaredScore + bonus + maxScore) : maxScore;
                      
                      return (
                          <div key={profile.id} className={`rounded-xl border-2 transition-all overflow-hidden flex flex-col ${isSelected ? 'flex-1 min-h-0 border-yellow-500 bg-zinc-900/60' : 'shrink-0 border-zinc-800 bg-zinc-900/40 hover:border-zinc-600'}`}>
                              <button
                                onClick={() => setSelectedProfile(isSelected ? null : profile)}
                                className={`w-full p-5 text-left transition-all flex items-center justify-between shrink-0 ${isSelected ? 'bg-yellow-900/20' : ''}`}
                              >
                                <div className="flex flex-col gap-1 overflow-hidden">
                                    <span className="text-xl font-rajdhani font-bold text-white truncate">{profile.name}</span>
                                    <div className="flex items-center gap-3 text-xs text-zinc-500 font-orbitron uppercase tracking-widest">
                                        <span className="flex items-center gap-1"><ListChecks size={12} /> {profile.selectedTricks.length} Tricks</span>
                                        {profile.alternativeTrick && <span className="flex items-center gap-1 text-purple-400 font-bold"><Replace size={12} /> Alt Available / 有备选</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4 shrink-0">
                                    <div className="flex flex-col items-end">
                                        <span className={`text-xl font-rajdhani font-black leading-none ${isSelected ? 'text-yellow-400' : 'text-zinc-500'}`}>{displayScore}</span>
                                        <span className="text-[8px] font-orbitron text-zinc-600 uppercase tracking-widest mt-1">PTS MAX</span>
                                    </div>
                                    <Play size={20} className={isSelected ? 'text-yellow-500' : 'text-zinc-700'} />
                                </div>
                              </button>
                              
                              {isSelected && (
                                  <div className="flex-1 flex flex-col min-h-0 px-5 pb-5 pt-3 border-t border-yellow-500/20 bg-black/20 relative">
                                      <div className="shrink-0 flex items-center justify-between mb-3">
                                          <div className="text-[10px] text-zinc-500 font-orbitron uppercase tracking-widest">Trick List / 招式详情</div>
                                          <button 
                                              onClick={(e) => {
                                                  e.stopPropagation();
                                                  navigate('/tournament/editor');
                                              }}
                                              className="flex items-center gap-1 text-[10px] font-orbitron tracking-widest text-zinc-400 hover:text-yellow-400 transition-colors bg-zinc-900/80 px-2 py-1 rounded border border-zinc-700 hover:border-yellow-500/50"
                                          >
                                              <Settings size={12} /> EDIT / 编辑
                                          </button>
                                      </div>
                                      <div className="flex-1 overflow-y-auto custom-scrollbar pr-2 space-y-2">
                                          {profile.selectedTricks.length === 0 && !profile.alternativeTrick ? (
                                              <div className="flex min-h-full flex-col items-center justify-center gap-3 py-8 text-center">
                                                  <p className="font-rajdhani text-sm text-zinc-500">当前配置无招式,请先配置招式</p>
                                                  <button
                                                      onClick={(e) => {
                                                          e.stopPropagation();
                                                          navigate('/tournament/editor');
                                                      }}
                                                      className="flex items-center gap-2 text-base font-orbitron tracking-widest text-yellow-400 bg-zinc-900/80 px-6 py-3 rounded-lg border border-yellow-500/50 hover:bg-yellow-500/10 transition-colors"
                                                  >
                                                      <Settings size={18} /> 去配置招式 / CONFIGURE
                                                  </button>
                                              </div>
                                          ) : (
                                          <>
                                          {profile.selectedTricks.map((sel, idx) => {
                                              const ml = config.masterLists.find(l => l.id === sel.masterListId);
                                              const t = ml?.tricks.find(t => t.id === sel.trickId);
                                              if (!t) return null;
                                              return (
                                                  <div key={idx} className="flex justify-between items-center bg-zinc-900/80 p-2 rounded border border-zinc-800/50 shrink-0">
                                                      <div className="flex items-center gap-2">
                                                          <span className="text-zinc-600 font-mono text-[10px] w-4">{idx + 1}</span>
                                                          <span className="font-rajdhani text-sm text-zinc-300">{t.name}</span>
                                                      </div>
                                                      <span className="text-yellow-500/60 font-orbitron text-[10px]">{t.score} pts</span>
                                                  </div>
                                              );
                                          })}
                                          {profile.alternativeTrick && (() => {
                                              const sel = profile.alternativeTrick;
                                              const ml = config.masterLists.find(l => l.id === sel.masterListId);
                                              const t = ml?.tricks.find(t => t.id === sel.trickId);
                                              if (!t) return null;
                                              return (
                                                  <div className="flex justify-between items-center bg-purple-900/20 p-2 rounded border border-purple-500/30 shrink-0">
                                                      <div className="flex items-center gap-2">
                                                          <span className="text-purple-500 font-mono text-[10px] w-4">Alt</span>
                                                          <span className="font-rajdhani text-sm text-purple-300">{t.name}</span>
                                                      </div>
                                                      <span className="text-purple-400 font-orbitron text-[10px]">{t.score} pts</span>
                                                  </div>
                                              );
                                          })()}
                                          </>
                                          )}
                                      </div>
                                  </div>
                              )}
                          </div>
                      );
                  })}
              </div>

              {selectedProfile && (
                  <div className="shrink-0 mt-4 sm:mt-8 space-y-4 animate-in fade-in slide-in-from-bottom-4">
                      {selectedCategory === 'FINAL' && (
                          <div className="bg-zinc-900/80 p-4 rounded-xl border border-zinc-700">
                             <label className="text-zinc-500 font-rajdhani text-xs uppercase tracking-widest block mb-2">Initial Final Score / 决赛初始分</label>
                             <input type="number" value={initialScore} onChange={e => setInitialScore(e.target.value)} className="w-full bg-black border border-zinc-700 rounded-lg py-3 px-4 text-3xl font-mono text-yellow-500 outline-none focus:border-yellow-500 transition-colors" />
                          </div>
                      )}
                      <button onClick={handleStart} className="w-full py-5 bg-yellow-600 hover:bg-yellow-500 text-black font-black font-orbitron text-2xl uppercase tracking-[0.2em] rounded-xl shadow-lg transition-all">START / 开始</button>
                  </div>
              )}
          </div>
      );
  }

  if (phase === 'COUNTDOWN') {
      return (
          <div className="w-full h-full flex flex-col items-center justify-center bg-black relative overflow-hidden">
               <div className="absolute top-0 left-0 w-full p-4 flex justify-between items-center z-50 pt-10">
                  <button onClick={handleBackRequest} className="p-2 text-zinc-500 hover:text-white rounded-lg transition-colors flex items-center gap-2 backdrop-blur-sm bg-black/20">
                    <ArrowLeft size={24} /> <span className="text-sm font-orbitron hidden sm:inline">EXIT</span>
                  </button>
               </div>

               <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_rgba(234,179,8,0.2),_transparent_70%)] animate-pulse"></div>
               <div key={countdown} className="relative z-10 text-[12rem] md:text-[20rem] font-black font-rounded text-transparent bg-clip-text bg-gradient-to-b from-yellow-300 to-yellow-600 drop-shadow-[0_0_50px_rgba(234,179,8,0.5)] animate-in zoom-in-50 duration-300">{countdown}</div>
               <div className="text-yellow-500/50 font-rajdhani uppercase text-xl md:text-3xl mt-8 text-center flex flex-col items-center gap-2">
                         <span className="tracking-[1em] pl-[1em]">GET READY</span>
                         <span className="tracking-[0.5em] pl-[0.5em]">准备</span>
                     </div>
               {exitConfirmModal}
          </div>
      );
  }

  return (
      <div className="w-full max-w-3xl mx-auto p-1 md:p-2 h-full flex flex-col overflow-hidden relative pt-2 md:pt-10">
           <div className="w-full flex justify-between items-center shrink-0 mb-1 md:mb-2 px-1 md:px-2">
               <div className="flex items-center gap-2">
                   <button onClick={handleBackRequest} className="p-1 md:p-2 text-zinc-500 hover:text-white rounded-lg transition-colors flex items-center gap-2 z-10 relative">
                      <ArrowLeft size={24} className="w-5 h-5 md:w-6 md:h-6" /> <span className="text-sm font-orbitron hidden sm:inline">EXIT</span>
                   </button>
               </div>
               <div className="text-right flex flex-col">
                  <span className="text-[10px] md:text-xs font-orbitron text-yellow-500 font-bold tracking-widest truncate max-w-[150px] md:max-w-none">{selectedProfile?.name}</span>
                  <span className="text-[8px] md:text-[10px] text-zinc-600 font-rajdhani uppercase">{getCategoryDisplayName(selectedCategory).split(' / ')[0]}</span>
               </div>
           </div>

           <div className="w-full shrink-0 mb-1 md:mb-2 flex flex-col items-center justify-center">
                <div className="flex items-center gap-1 md:gap-2">
                  <Clock className={`${timeLeft === 0 ? 'text-red-500 animate-pulse' : timeLeft < 30000 ? 'text-orange-500 animate-pulse' : 'text-zinc-500'}`} size={16} />
                  <span className="text-zinc-500 font-orbitron text-[8px] md:text-[10px] uppercase tracking-widest hidden sm:inline">Time Remaining</span>
                  <span className="text-zinc-500 font-orbitron text-[8px] md:text-[10px] uppercase tracking-widest sm:hidden">Time</span>
                </div>
                <span className={`text-[clamp(3rem,20vw,10rem)] font-rounded font-extrabold tracking-wider leading-none ${timeLeft === 0 ? 'text-red-500 animate-pulse' : timeLeft < 30000 ? 'text-orange-500' : 'text-white'}`}>
                  {formatTime(timeLeft)}
                </span>
           </div>

           <div className="flex flex-row items-center justify-center gap-2 mb-2 shrink-0">
               <div className="bg-zinc-900/80 p-2 md:p-4 rounded-xl md:rounded-2xl border-2 border-zinc-800 flex flex-col items-center justify-center min-w-0 flex-1 backdrop-blur-md shadow-lg">
                    <span className="text-zinc-500 font-orbitron text-[8px] md:text-[10px] uppercase tracking-widest mb-1">Completed Tricks</span>
                    <span className="text-3xl sm:text-5xl md:text-7xl font-black font-rajdhani text-green-500 drop-shadow-[0_0_10px_rgba(34,197,94,0.35)] flex items-baseline gap-1 leading-none">
                      {completedTrickCount}<span className="text-lg sm:text-2xl md:text-4xl text-zinc-600">/ {currentTrickList.length}</span>
                    </span>
               </div>
               <div className="bg-zinc-900/80 p-2 md:p-4 rounded-xl md:rounded-2xl border-2 border-zinc-800 flex flex-col items-center justify-center min-w-0 flex-1 backdrop-blur-md shadow-lg">
                    <span className="text-zinc-500 font-orbitron text-[8px] md:text-[10px] uppercase tracking-widest mb-1">Total Points</span>
                    <span className="text-3xl sm:text-5xl md:text-7xl font-black font-rajdhani text-yellow-500 neon-glow-blue">{currentScore}</span>
               </div>
           </div>

           <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-1 min-h-0">
               {currentTrickList.map((trick, idx) => {
                   const status = trickStatuses[trick.id];
                   const isAlternative = alternativeTrickObj && trick.id === alternativeTrickObj.id;
                   let bgClass = 'bg-zinc-900/50 border-zinc-800';
                   if (status === 'success') bgClass = 'bg-green-900/20 border-green-500/50';
                   if (status === 'loss') bgClass = 'bg-red-900/20 border-red-500/50';
                   if (status === 'skip') bgClass = 'bg-zinc-800 border-zinc-700 opacity-50';
                   if (isAlternative && (!status || status === 'pending')) bgClass = 'bg-purple-900/10 border-purple-500/30 shadow-[0_0_10px_rgba(168,85,247,0.1)]';
                   const displayScore = selectedCategory === 'FINAL' ? Math.pow(trick.score, 2) : trick.score;
                   return (
                       <div key={trick.id + idx} className={`relative flex flex-col p-2 md:p-4 rounded-xl border transition-all ${bgClass}`}>
                           {isAlternative && <div className="absolute -top-2 left-4 px-2 py-0.5 bg-purple-600 text-white text-[9px] font-orbitron font-bold uppercase tracking-widest rounded-full shadow-lg z-10">Alternative / 备选</div>}
                           <div className={`flex justify-between items-center ${selectedCategory === 'FINAL' ? 'mb-1 md:mb-3' : ''}`}>
                               <div className="flex items-center gap-2 md:gap-4 pr-2">
                                   <span className="text-zinc-600 font-mono text-sm w-4 md:w-6">{idx + 1}</span>
                                   <span className={`text-base sm:text-xl md:text-3xl font-rajdhani font-bold whitespace-pre-wrap leading-tight ${status === 'success' ? 'text-white' : 'text-zinc-300'}`}>{trick.name}</span>
                               </div>
                               <div className="flex items-center gap-2 md:gap-3 shrink-0">
                                   <span className={`font-orbitron text-sm font-bold ${status === 'success' ? 'text-yellow-400' : 'text-zinc-600'}`}>{displayScore} pts</span>
                                   {selectedCategory !== 'FINAL' && (
                                       <button onClick={() => handleUpdateStatus(trick.id, status === 'success' ? 'pending' : 'success')} className={`px-2 py-1 md:px-3 md:py-2 rounded-lg flex items-center justify-center gap-1 transition-all font-bold ${status === 'success' ? 'bg-green-600 text-white shadow-[0_0_15px_rgba(22,163,74,0.4)]' : 'bg-zinc-800 text-zinc-500 hover:bg-green-900/30 hover:text-green-400'}`}>
                                           <CheckCircle2 size={16} className="sm:w-5 sm:h-5" />
                                           <div className="flex flex-col items-center leading-none">
                                               <span className="text-[8px] md:text-[10px] uppercase tracking-widest">Success</span>
                                               <span className="text-[8px] md:text-[10px] mt-[2px]">成功</span>
                                           </div>
                                       </button>
                                   )}
                               </div>
                           </div>
                           {selectedCategory === 'FINAL' && (
                               <div className="flex gap-1 md:gap-2">
                                   <button onClick={() => handleUpdateStatus(trick.id, 'success')} className={`flex-1 py-1.5 md:py-2 rounded-lg flex items-center justify-center gap-1 md:gap-2 transition-all font-bold ${status === 'success' ? 'bg-green-600 text-white shadow-[0_0_15px_rgba(22,163,74,0.4)]' : 'bg-zinc-800 text-zinc-500 hover:bg-green-900/30 hover:text-green-400'}`}><CheckCircle2 size={14} className="sm:w-4 sm:h-4" /> <span className="text-[9px] md:text-[10px] uppercase tracking-widest">SUCCESS / 成功</span></button>
                                   <button onClick={() => handleUpdateStatus(trick.id, 'loss')} className={`flex-1 py-1.5 md:py-2 rounded-lg flex items-center justify-center gap-1 md:gap-2 transition-all font-bold ${status === 'loss' ? 'bg-red-600 text-white shadow-[0_0_15px_rgba(220,38,38,0.4)]' : 'bg-zinc-800 text-zinc-500 hover:bg-red-900/30 hover:text-red-400'}`}><XCircle size={14} className="sm:w-4 sm:h-4" /> <span className="text-[9px] md:text-[10px] uppercase tracking-widest">FAIL / 失败</span></button>
                                   <button onClick={() => handleUpdateStatus(trick.id, 'skip')} className={`flex-1 py-1.5 md:py-2 rounded-lg flex items-center justify-center gap-1 md:gap-2 transition-all font-bold ${status === 'skip' ? 'bg-zinc-600 text-white' : 'bg-zinc-800 text-zinc-500 hover:bg-zinc-700'}`}><SkipForward size={14} className="sm:w-4 sm:h-4" /> <span className="text-[9px] md:text-[10px] uppercase tracking-widest">SKIP / 跳过</span></button>
                               </div>
                           )}
                       </div>
                   );
               })}
               <div className="h-2" />
           </div>

           <div className="shrink-0 mt-2 pt-3 border-t border-zinc-800 pb-1">
               <div className="relative">
                   <button onClick={handleManualFinish} className="relative w-full overflow-hidden rounded-xl border-2 border-yellow-600/60 bg-zinc-900 shadow-lg transition-all">
                       <div className="flex flex-col items-center py-4 text-white">
                           <span className="font-black font-orbitron text-xl md:text-2xl uppercase tracking-[0.2em]">FINISH ROUND</span>
                           <span className="text-sm font-rajdhani tracking-widest mt-1">结束并结算分数</span>
                       </div>
                       <div className="absolute inset-0 flex flex-col items-center py-4 bg-yellow-500 text-black" style={{ clipPath: `inset(0 ${100 - roundFillPercent}% 0 0)` }}>
                           <span className="font-black font-orbitron text-xl md:text-2xl uppercase tracking-[0.2em]">FINISH ROUND</span>
                           <span className="text-sm font-rajdhani tracking-widest mt-1">结束并结算分数</span>
                       </div>
                   </button>
                   {/* 填充边缘的模糊过渡带：弱化黄/黑分界线 */}
                   {roundFillPercent > 0 && roundFillPercent < 100 && (
                       <div
                           className="pointer-events-none absolute inset-y-0 z-10"
                           style={{
                               left: `${roundFillPercent}%`,
                               width: 18,
                               transform: 'translateX(-32%)',
                               background: 'linear-gradient(to right, rgba(250, 204, 21, 1) 0%, rgba(250, 204, 21, 0.85) 28%, rgba(250, 204, 21, 0.35) 62%, rgba(250, 204, 21, 0) 100%)',
                               filter: 'blur(3px)',
                           }}
                       />
                   )}
                   {/* 填充边缘发散的粗粒子：不裁剪，可超出按钮边框 */}
                   {roundFillPercent > 0 && roundFillPercent < 100 && (
                       <div className="pointer-events-none absolute inset-y-0 z-20 w-0" style={{ left: `${roundFillPercent}%` }}>
                           {FILL_EDGE_PARTICLES.map((p, i) => (
                               <span
                                   key={i}
                                   className={`btn-fill-particle${p.dark ? ' btn-fill-particle--dark' : ''}`}
                                   style={{
                                       top: `${p.top}%`,
                                       width: p.size,
                                       height: p.size,
                                       animationDuration: `${p.duration}ms`,
                                       animationDelay: `${p.delay}ms`,
                                       ['--dx' as string]: `${p.dx}px`,
                                       ['--dy' as string]: `${p.dy}px`,
                                       ...(p.color ? { ['--particle-color' as string]: p.color } : {}),
                                   } as React.CSSProperties}
                               />
                           ))}
                       </div>
                   )}
               </div>
           </div>

           {phase === 'FINISHED' && (
               <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-md animate-in fade-in duration-500 p-4">
                   <div className="flex flex-col items-center text-center p-8 bg-zinc-900 border-2 border-yellow-500 rounded-3xl shadow-[0_0_50px_rgba(234,179,8,0.2)] w-full max-w-md">
                       <h2 className="text-3xl font-orbitron font-bold text-white mb-1">ROUND COMPLETE</h2>
                       <div className="text-zinc-500 font-rajdhani uppercase tracking-widest text-xs mb-6">{selectedProfile?.name}</div>
                       <div className="mb-10">
                           <div className="text-[10px] text-zinc-500 uppercase tracking-widest font-orbitron mb-1">Final Score</div>
                           <div className="text-8xl font-black font-rajdhani text-yellow-500 drop-shadow-[0_0_20px_rgba(234,179,8,0.5)]">{currentScore}</div>
                       </div>
                       <div className="flex flex-col w-full gap-3">
                           <button onClick={() => setPhase('SELECT_PROFILE')} className="py-4 bg-yellow-600 hover:bg-yellow-500 text-black font-bold rounded-xl uppercase tracking-wider font-orbitron flex items-center justify-center gap-2"><RotateCcw size={20} /> Replay Configuration</button>
                           <button onClick={onBack} className="py-4 border border-zinc-700 hover:bg-zinc-800 text-zinc-400 rounded-xl uppercase tracking-wider font-orbitron text-sm">Main Menu</button>
                       </div>
                   </div>
               </div>
           )}

           {exitConfirmModal}
      </div>
  );
};

export default TournamentGame;
